import '@/app/styles/side-bar.css'
import '@/app/styles/page-content-shell.css'
import React, { useState, useEffect, useContext, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, RequireAdmin, RequireGod, AuthGate } from '@/features/auth/AuthContext';
import {
    WIZARD_ASSISTANT_QUERY_PARAM,
    WIZARD_ASSISTANT_QUERY_VALUE,
} from '@/features/assistant/constants';
import GodConsole from '@/features/admin/components/GodConsole';
import { Layout, ConfigProvider } from 'antd';

import { Playground } from '@/pages/Playground.jsx';
import { Settings } from '@/pages/Settings.jsx';
import { DiscoverWorkspaces } from '@/pages/DiscoverWorkspaces.jsx';

import { Trainings } from '@/pages/Trainings.jsx';
import { Datasets } from '@/pages/Datasets.jsx';
import SupportRequest from '@/pages/SupportRequest.jsx'
import { Deployments } from '@/pages/Deployments.jsx';
import { Deployment } from '@/pages/Deployment.jsx';
import { ExploreDatasets } from '@/pages/ExploreDatasets.jsx';
import { HomePage } from '@/pages/HomePage.jsx';
import { GlobalContext } from '@/app/providers/globalState';
import { Documentation } from '@/features/documentation/components/Documentation';
import UsabilityDashboard from '@/pages/UsabilityDashboard.jsx';
import DeploymentMonitoringPage from '@/pages/DeploymentMonitoringDashboard.jsx';
import { useUpdatesListener } from '@/app/realtime/useUpdatesListener';
import FloatingProgressBar from '@/app/layout/FloatingProgressBar';
import HorizontalNavBar from '@/app/layout/HorizontalNavBar';
import { PlatformUpgraderProvider } from '@/features/platform-upgrader';
import UsecaseHeader from '@/features/tasks/components/UsecaseHeader';
import TaskPromptSetupModal from '@/features/tasks/components/TaskPromptSetupModal';
import TaskPage from '@/pages/TaskPage.jsx';
import CanonicalTaskRedirect from '@/features/tasks/components/CanonicalTaskRedirect';
import CreateTaskWizardPage from '@/features/tasks/components/CreationWizard/CreateTaskWizardPage';
import { allSubtasksShareSameConfig } from '@/features/tasks/utils/promptPropagation';

const SideBar = () => {

    const { role, mcpEnabled } = useAuth();
    const {
        allUsecases,
        setAllUsecases,
        setCurrentUsecaseId,
        getCurrentUsecase,
        currentSubGroup,
        updatePromptAndTeacher,
    } = useContext(GlobalContext);

    useUpdatesListener()

    // React Router hooks
    const navigate = useNavigate();
    const location = useLocation();
    const hasProcessedUrlRef = useRef(false);

    const [currentScreen, setCurrentScreen] = useState("hello-user");
    const [contactUsModalOpen, setContactUsModalOpen] = useState(false);
    const [isTaskPromptSetupOpen, setIsTaskPromptSetupOpen] = useState(false);
    // Opened only from the header "Task Prompt" button (manual). Saves
    // propagate to every sibling that shares the same config.
    const [taskPromptSetupSource, setTaskPromptSetupSource] = useState('manual-shared');

    const suppressNextAutoScrollToTopRef = useRef(false);

    // Sync currentScreen with URL
    useEffect(() => {
        const path = location.pathname.slice(1);
        setCurrentScreen(path || "hello-user");
    }, [location.pathname]);

    // Scroll to top whenever the current screen changes
    useLayoutEffect(() => {
        if (suppressNextAutoScrollToTopRef.current) {
            suppressNextAutoScrollToTopRef.current = false;
            return;
        }
        // Use requestAnimationFrame to ensure DOM is fully rendered
        setTimeout(() => {
            requestAnimationFrame(() => {
                window.scrollTo(0, 0);
            });
        }, 1000);
    }, [currentScreen]);

    // Extract current screen from URL on initial load
    useEffect(() => {
        if (hasProcessedUrlRef.current) {
            return;
        }

        const path = location.pathname.slice(1); // Remove leading slash
        if (path) {
            // Extract usecase ID from URL if present
            const urlParams = new URLSearchParams(location.search);
            const usecaseId = urlParams.get('usecaseId');

            if (usecaseId && allUsecases.length > 0) {
                const usecase = allUsecases.find(usecase => usecase._id === usecaseId);
                if (usecase) {
                    setCurrentUsecaseId(usecaseId);
                    hasProcessedUrlRef.current = true;
                }
            }

            // For pages with custom parameters (like response-parser)
            if (path === 'response-parser') {
                const prompt = urlParams.get('prompt');
                const teacher = urlParams.get('teacher');

                if (prompt && teacher) {
                    try {
                        const parsedPrompt = JSON.parse(decodeURIComponent(prompt));
                        const parsedTeacher = decodeURIComponent(teacher);
                        updatePromptAndTeacher(parsedPrompt, parsedTeacher);
                    } catch (error) {
                        console.error('Error parsing URL parameters:', error);
                    }
                }
            }
        }
    }, [location.pathname, location.search, allUsecases, setCurrentUsecaseId, updatePromptAndTeacher]);

    const moveToScreen = (newUsecaseId, screenId, fromEvaluation = false, additionalParams = '') => {
        setTimeout(() => {
            setCurrentUsecaseId(newUsecaseId);
            setCurrentScreen(screenId);
            const url = `/${screenId}?usecaseId=${newUsecaseId}${fromEvaluation ? '&fromEvaluation=true' : ''}${additionalParams}`;
            navigate(url);
        }, 10);
    };

    // Both create entry points open the full-page creation wizard
    // (CreateTaskWizardPage) — the legacy Create Project / New Task modals
    // are retired.
    const openCreateProjectModal = () => navigate('/create-task');

    // "Add new task" opens the wizard. The page shows the Spark assistant on
    // every real visit; `assistant=1` is kept so a shared link matches
    // "Jump right in".
    const openNewTaskModal = (projectName) => {
        const params = new URLSearchParams();
        if (projectName) params.set('project', projectName);
        params.set(WIZARD_ASSISTANT_QUERY_PARAM, WIZARD_ASSISTANT_QUERY_VALUE);
        navigate(`/create-task?${params.toString()}`);
    };

    const generalScreens = ['hello-user', 'create-task', 'datasets', 'models', 'playground', 'settings', 'discover-workspaces', 'user-management', 'analytics', 'deployment-monitoring', 'docs', 'installation', 'quick-start', 'docs-configuration', 'querying', 'logging', 'external-deploy', 'mcp-server', 'support-doc', 'request-access', 'admin'];
    // Screens where a task is the active context. The task page itself renders
    // its own header (breadcrumb + title + actions), so UsecaseHeader is
    // suppressed there.
    const isTaskContextScreen = !generalScreens.includes(currentScreen);
    const isTaskRoute = location.pathname.startsWith('/task/');
    const showUsecaseHeader = isTaskContextScreen && !isTaskRoute;

    // The Playground (sparkles) button + drawer only make sense while a task is open.
    const openTaskPromptSetup = (source = 'manual-shared') => {
        setTaskPromptSetupSource(source);
        setIsTaskPromptSetupOpen(true);
    };

    const closeTaskPromptSetup = () => {
        setIsTaskPromptSetupOpen(false);
        setTaskPromptSetupSource('manual-shared');
    };

    // For composed tasks, "Task Prompt" only makes sense as a single shared
    // editor when every subtask already has the exact same prompt + teacher
    // model. We require ``currentSubGroup.usecases`` to be hydrated before
    // making any decision: ``currentSubGroup`` lags ``getCurrentUsecase()``
    // by one render after data lands (it derives from the ``currentUsecase``
    // state set by an effect in globalState), so falling back to a single-
    // element list would briefly mark a composed task as "shared" and flash
    // the header button on refresh. Waiting for the sub-group to populate
    // avoids that; single-objective tasks still resolve correctly because
    // their sub-group has exactly one entry once consistent.
    const subGroupUsecases = currentSubGroup?.usecases;
    const subGroupReady = Array.isArray(subGroupUsecases) && subGroupUsecases.length > 0;
    const subtasksShareSameConfig =
        subGroupReady && allSubtasksShareSameConfig(subGroupUsecases);

    return (
        <ConfigProvider>
            <PlatformUpgraderProvider>
            <FloatingProgressBar />
            <Layout
                style={{
                    height: '100vh',
                    overflow: 'hidden',
                    backgroundColor: 'var(--semantic-surface-main)',
                }}
            >
                <HorizontalNavBar />
                <AuthGate>
                {/* `data-walkthrough-scroller` + `position: relative`: the walkthrough
                    coach mark renders inside this scroller so it moves with the page
                    content natively (see features/walkthrough/utils/overlayHost.js). */}
                <Layout
                    className="spark-native-scrollbar"
                    data-walkthrough-scroller=""
                    style={{
                        position: 'relative',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        scrollbarGutter: 'stable',
                        flexDirection: 'column',
                        backgroundColor: 'var(--semantic-surface-main)',
                    }}
                >
                    {showUsecaseHeader && (
                        <UsecaseHeader
                            onCreateProject={openCreateProjectModal}
                            onAddTask={openNewTaskModal}
                            onOpenTaskPrompt={
                                subtasksShareSameConfig
                                    ? () => openTaskPromptSetup('manual-shared')
                                    : undefined
                            }
                        />
                    )}
                    <Routes>
                        <Route path="/" element={
                            <HomePage
                                moveToScreen={moveToScreen}
                                setAllUsecases={setAllUsecases}
                                moveToExplore={() => {
                                    setCurrentScreen("explore")
                                    navigate("/explore")
                                }}
                            />
                        } />
                        {/* Short form every link builder emits; redirected to the canonical URL below. */}
                        <Route path="/task/:taskName" element={
                            <CanonicalTaskRedirect>
                                <TaskPage
                                    moveToScreen={moveToScreen}
                                    onCreateProject={openCreateProjectModal}
                                    onAddTask={openNewTaskModal}
                                />
                            </CanonicalTaskRedirect>
                        } />
                        {/* Canonical: the workspace the task lives in, then the task. Legacy
                            /task/<project>/<task> deep links share the shape and still resolve. */}
                        <Route path="/task/:workspaceName/:taskName" element={
                            <TaskPage
                                moveToScreen={moveToScreen}
                                onCreateProject={openCreateProjectModal}
                                onAddTask={openNewTaskModal}
                            />
                        } />
                        <Route path="/create-task" element={<CreateTaskWizardPage />} />
                        <Route path="/playground" element={<Playground />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/discover-workspaces" element={<DiscoverWorkspaces />} />
                        {/* Phase 4: God-only workspaces console. /pending-approval, /create-workspace and /request-access are full-page routes in AppRoutes. */}
                        <Route path="/admin" element={<RequireGod><GodConsole /></RequireGod>} />

                        <Route path="/user-management" element={<RequireAdmin><Navigate to="/settings?tab=users" replace /></RequireAdmin>} />
                        <Route path="/analytics" element={
                            <UsabilityDashboard
                                allUsecases={allUsecases}
                            />
                        } />
                        <Route path="/deployment-monitoring" element={<DeploymentMonitoringPage />} />
                        <Route path="/models" element={
                            <Trainings
                                allUsecases={allUsecases}
                                moveToScreen={moveToScreen}
                            />
                        } />
                        <Route path="/datasets" element={
                            <Datasets
                                moveToExplore={() => {
                                    setCurrentScreen("explore")
                                    navigate("/explore")
                                }}
                            />
                        } />
                        <Route path="/explore" element={
                            <ExploreDatasets
                                dataset={null}
                                goToTrain={() => navigate("/playground")}
                                moveToPlayground={() => navigate("/playground")}
                            />
                        } />
                        <Route path="/deployment" element={
                            <Deployment
                                moveToPlayground={() => navigate("/playground")}
                                reset={() => {}}
                                currentModel=""
                            />
                        } />
                        <Route path="/deployments" element={
                            <Deployments
                                moveToPlayground={() => navigate("/playground")}
                                reset={() => {}}
                            />
                        } />
                        <Route path="/docs" element={
                            <Documentation
                                section="quick-start"
                            />
                        } />
                        <Route path="/installation" element={
                            <Documentation
                                section="installation"
                            />
                        } />
                        <Route path="/quick-start" element={
                            <Documentation
                                section="quick-start"
                            />
                        } />
                        <Route path="/inference" element={
                            <Documentation
                                section="inference"
                            />
                        } />
                        <Route path="/docs-configuration" element={
                            <Documentation
                                section="docs-configuration"
                            />
                        } />
                        <Route path="/data-collection" element={
                            <Documentation
                                section="data-collection"
                            />
                        } />
                        <Route path="/best-practices" element={
                            <Documentation
                                section="best-practices"
                            />
                        } />
                        <Route path="/env-variables" element={
                            <Documentation
                                section="env-variables"
                            />
                        } />
                        <Route path="/querying" element={
                            <Documentation
                                section="querying"
                            />
                        } />
                        <Route path="/logging" element={
                            <Documentation
                                section="logging"
                            />
                        } />
                        <Route path="/external-deploy" element={
                            <Documentation
                                section="external-deploy"
                            />
                        } />
                        {mcpEnabled && (
                            <Route path="/mcp-server" element={
                                <Documentation
                                    section="mcp-server"
                                />
                            } />
                        )}
                        <Route path="/support-doc" element={
                            <Documentation
                                section="support-doc"
                            />
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Layout>
                </AuthGate>
            </Layout>
            <TaskPromptSetupModal
                open={isTaskPromptSetupOpen}
                onClose={closeTaskPromptSetup}
                usecase={getCurrentUsecase()}
                role={role}
                propagationMode={
                    taskPromptSetupSource === 'manual-shared' ? 'all-siblings' : 'auto'
                }
                onSaved={(payload) => {
                    // Keep local context state in sync immediately (without waiting for a refresh).
                    updatePromptAndTeacher(
                        payload?.prompt,
                        payload?.teacher_model,
                        undefined,
                        payload?.teacher_provider,
                        payload?.teacher_model_display
                    );
                }}
            />
            {contactUsModalOpen && <SupportRequest
                isOpen={contactUsModalOpen}
                handleOk={() => setContactUsModalOpen(false)}
                setIsOpen={setContactUsModalOpen}
            />}
            </PlatformUpgraderProvider>
        </ConfigProvider>
    );
};

export default SideBar;
